/**
 * Copying a rectangle out of a grid, and pasting one back in.
 *
 * The rectangle-to-cells arithmetic is here and pure; the events and the
 * request are the grid's. Both directions are clipped to what exists: a paste
 * larger than the rows or the columns below and to the right of where it starts
 * writes as much as fits and stops.
 *
 * That is a real limitation rather than an oversight. A paste that outgrew the
 * table would have to create rows, and creating rows is a different mutation
 * with its own ordering problem -- a value cannot be written to a row whose
 * `POST` has not come back with an id. It arrives with row creation.
 */
import type { RowData } from '@/api/types'
import type { FieldContext } from '../fields/behavior/behavior'
import { cellText } from '../fields/read'
import { editorFor } from '../fields/ui/edit'
import { fieldKey } from '@/api/types'
import type { RowWindow } from '../rows/rowWindow'
import { rowAt } from '../rows/rowWindow'
import type { CellGrid } from '../rows/rowClipboard'
import type { GridColumn } from './columns'
import type { SelectionRect } from './selection'

/** The rows and columns a rectangle covers, as far as they exist. */
export function cellsIn(
  rect: SelectionRect,
  window: RowWindow,
  columns: readonly GridColumn[],
): { rows: RowData[]; columns: GridColumn[] } {
  const rows: RowData[] = []
  for (let i = rect.top; i < rect.top + rect.rows; i += 1) {
    const row = rowAt(window, i)
    if (row !== undefined) rows.push(row)
  }

  return { rows, columns: columns.slice(rect.left, rect.left + rect.columns) }
}

/**
 * What a selection copies as: the text a person would have read.
 *
 * Rendered rather than raw, because the text is what every other application
 * receives. The raw values travel beside it, in `localStorage`, for a paste back
 * into this one.
 */
export function copyGrid(
  rows: readonly RowData[],
  columns: readonly GridColumn[],
  context?: FieldContext,
): { text: CellGrid<string>; values: CellGrid<unknown> } {
  return {
    // Through the context, so a copy carries the name the workspace has for
    // somebody rather than the one the row was written with.
    text: rows.map((row) =>
      columns.map((column) => cellText(column.field, row, context)),
    ),
    values: rows.map((row) =>
      columns.map((column) => row[fieldKey(column.field.id)] ?? null),
    ),
  }
}

/**
 * Turns a pasted rectangle into the writes it implies.
 *
 * `values` is used when the clipboard is this application's own and the raw
 * values came with it; otherwise the text is read through each field's editor,
 * which is the same conversion typing that text into the cell would do. A column
 * with no editor -- a formula, a read-only field -- is skipped rather than
 * failed, so pasting a block that happens to span one writes the rest.
 *
 * A row whose every column was skipped produces no entry at all, which is what
 * stops a paste over nothing but read-only columns from being a request that
 * changes nothing.
 */
export function pasteInto(
  pasted: { text: CellGrid<string>; values: CellGrid<unknown> | null },
  rows: readonly RowData[],
  columns: readonly GridColumn[],
  context?: FieldContext,
): { rowId: number; values: Record<string, unknown> }[] {
  const updates: { rowId: number; values: Record<string, unknown> }[] = []

  rows.forEach((row, rowIndex) => {
    const values: Record<string, unknown> = {}

    columns.forEach((column, columnIndex) => {
      const editor = editorFor(column.field)
      if (editor === null) return

      // The values first and the text only as a fallback. A cell that was empty
      // has `null` here and nothing at all in the text, and the two are
      // different things: `null` is empty, `''` is a blank string.
      const raw = pasted.values?.[rowIndex]?.[columnIndex]
      if (raw !== undefined) {
        values[fieldKey(column.field.id)] = raw
        return
      }

      const text = pasted.text[rowIndex]?.[columnIndex]
      if (text === undefined) return
      // Text, not a draft: the two are the same question for every type but a
      // link row, whose draft carries the rows it was given and whose text
      // carries the names somebody read.
      const read = editor.fromText ?? editor.fromDraft
      const value = read(column.field, text, context)
      // A type that has no reading of text at all leaves its column alone. A
      // file field is the only one: pasting a word over somebody's attachments
      // would detach them for want of anything else to do with it.
      if (value === undefined) return
      values[fieldKey(column.field.id)] = value
    })

    if (Object.keys(values).length > 0) updates.push({ rowId: row.id, values })
  })

  return updates
}

/**
 * How far a paste reaches from where it starts.
 *
 * The pasted block decides the size. A one-cell selection pasted into with a
 * three-by-two block writes three by two, which is what every spreadsheet does
 * and what makes a paste worth having.
 *
 * Clipped sideways to the columns that exist, because a table cannot grow a
 * column to hold a value nobody has described. Not clipped downwards: a paste
 * longer than the table grows it, up to `limit` rows in total.
 *
 * Measured against whichever half of the clipboard is authoritative. A cell that
 * was copied while empty produces no text at all, so sizing from the text would
 * make pasting it a no-op rather than the way to empty a cell.
 */
export function pasteRect(
  start: SelectionRect,
  pasted: CellGrid<unknown>,
  columns: readonly GridColumn[],
  limit: number,
): SelectionRect {
  const width = Math.max(...pasted.map((row) => row.length), 0)

  return {
    top: start.top,
    left: start.left,
    rows: Math.min(pasted.length, limit),
    columns: Math.min(width, columns.length - start.left),
  }
}

/**
 * How many rows a paste can write from where it starts.
 *
 * A write needs a row id, and the client only has ids for the rows it is
 * holding. Past the last of those there are two possibilities and they are not
 * the same: if the window reaches the bottom of the view, what follows is the
 * end of the table and rows can be created there; if it does not, what follows
 * is a part of the table nobody has fetched, and there is nothing to write to.
 *
 * So a long paste into the middle of a large table is cut short at the buffer.
 * That is a real limit rather than an oversight -- the alternative is fetching
 * a thousand rows to write to them, which is a great deal of work to do on
 * somebody's behalf without asking.
 */
export function pasteReach(
  start: SelectionRect,
  window: RowWindow,
  limit: number,
): number {
  if (window.range.end === window.count) return limit
  return Math.min(Math.max(0, window.range.end - start.top), limit)
}

/**
 * Splits a paste into the rows that exist and the rows that have to be made.
 *
 * The two halves go to different endpoints, so the split has to happen before
 * either: what lands on an existing row is an edit, and what lands past the end
 * of the view is a row that does not exist yet.
 */
export function splitPaste(
  rect: SelectionRect,
  window: RowWindow,
): { existing: number; created: number } {
  const existing = Math.max(0, Math.min(rect.rows, window.count - rect.top))
  return { existing, created: rect.rows - existing }
}
